import {
  Button,
  Chip,
  Input,
  Textarea,
} from '@nextui-org/react';
import { HistoryItem, Settlement } from 'common/src/utils/types';
import { format } from 'date-fns';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Icon } from '@iconify/react';
import { toast } from 'react-toastify';
import { EmptyState, LoadingState } from '../ui';
import useUserStore from '../../../../common/src/stores/useUserStore';
import {
  addHistoryComment,
  addHistoryDocument,
  deleteHistoryItem,
  getDocumentUrl,
  getSettlementHistory,
  updateHistoryComment,
  uploadDocumentFile,
} from '../../services/historyService';
import SettlementSourcePreview, {
  RemoteSettlementDocument,
} from './settlement-source-preview';

export type SettlementSupportPanelMode = 'history' | 'documents';

interface SettlementEditSupportPanelProps {
  settlement: Settlement;
  mode: SettlementSupportPanelMode;
  onChange?: () => void;
}

const SettlementEditSupportPanel = ({
  settlement,
  mode,
  onChange,
}: SettlementEditSupportPanelProps) => {
  const { userProfile } = useUserStore();
  const [historyItems, setHistoryItems] = useState<HistoryItem[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [newComment, setNewComment] = useState('');
  const [editingCommentId, setEditingCommentId] = useState<number | null>(null);
  const [editingContent, setEditingContent] = useState('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [documentTitle, setDocumentTitle] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [previewLoadingId, setPreviewLoadingId] = useState<number | null>(null);
  const [downloadLoadingId, setDownloadLoadingId] = useState<number | null>(
    null
  );
  const [documentPreview, setDocumentPreview] =
    useState<RemoteSettlementDocument | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const loadHistory = useCallback(async () => {
    setIsLoading(true);
    try {
      setHistoryItems(await getSettlementHistory(settlement.id));
    } catch (error) {
      console.error('Error loading settlement support information:', error);
      toast.error('No se pudo cargar la información de la liquidación');
    } finally {
      setIsLoading(false);
    }
  }, [settlement.id]);

  useEffect(() => {
    void loadHistory();
    setDocumentPreview(null);
  }, [loadHistory]);

  const comments = historyItems.filter(
    (item) => item.type === 'comment' || item.type === 'action'
  );
  const documents = historyItems.filter((item) => item.type === 'document');

  const handleAddComment = async () => {
    if (!newComment.trim() || !userProfile?.id) return;
    try {
      await addHistoryComment(settlement.id, newComment.trim(), userProfile.id);
      await loadHistory();
      setNewComment('');
      onChange?.();
      toast.success('Comentario agregado');
    } catch (error) {
      console.error('Error adding settlement comment:', error);
      toast.error('No se pudo agregar el comentario');
    }
  };

  const handleSaveComment = async (commentId: number) => {
    if (!editingContent.trim()) return;
    try {
      await updateHistoryComment(commentId, editingContent.trim());
      await loadHistory();
      setEditingCommentId(null);
      setEditingContent('');
      onChange?.();
    } catch (error) {
      console.error('Error updating settlement comment:', error);
      toast.error('No se pudo actualizar el comentario');
    }
  };

  const handleDelete = async (itemId: number) => {
    try {
      await deleteHistoryItem(itemId);
      setHistoryItems((current) =>
        current.filter((item) => item.id !== itemId)
      );
      onChange?.();
    } catch (error) {
      console.error('Error deleting settlement history item:', error);
      toast.error('No se pudo eliminar el elemento');
    }
  };

  const selectDocumentFile = (file: File | null) => {
    setSelectedFile(file);
    setDocumentTitle(
      file ? file.name.split('.').slice(0, -1).join('.') || file.name : ''
    );
  };

  const handleUploadDocument = async () => {
    if (!selectedFile || !documentTitle.trim() || !userProfile?.id) return;
    setIsUploading(true);
    try {
      const extension = selectedFile.name.split('.').pop();
      const filePath = `${settlement.id}/${Date.now()}-${Math.random()
        .toString(36)
        .slice(2)}.${extension}`;
      await uploadDocumentFile(selectedFile, filePath);
      await addHistoryDocument(
        settlement.id,
        documentTitle.trim(),
        selectedFile.name,
        filePath,
        userProfile.id
      );
      await loadHistory();
      selectDocumentFile(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
      onChange?.();
      toast.success('Documento agregado');
    } catch (error) {
      console.error('Error uploading settlement document:', error);
      toast.error('No se pudo agregar el documento');
    } finally {
      setIsUploading(false);
    }
  };

  const getPreview = async (document: HistoryItem) => {
    if (!document.file_path) return;
    setPreviewLoadingId(document.id);
    try {
      const url = await getDocumentUrl(
        document.file_path,
        document.storage_bucket
      );
      if (!url) throw new Error('Could not create a signed document URL');
      setDocumentPreview({
        fileName: document.file_name || document.title || 'Documento',
        url,
      });
    } catch (error) {
      console.error('Error previewing settlement document:', error);
      toast.error('No se pudo previsualizar el documento');
    } finally {
      setPreviewLoadingId(null);
    }
  };

  const downloadDocument = async (document: HistoryItem) => {
    if (!document.file_path) return;
    setDownloadLoadingId(document.id);
    try {
      const url = await getDocumentUrl(
        document.file_path,
        document.storage_bucket
      );
      if (!url) throw new Error('Could not create a signed document URL');
      const response = await fetch(url);
      if (!response.ok) throw new Error(`Download failed (${response.status})`);
      const objectUrl = URL.createObjectURL(await response.blob());
      const link = window.document.createElement('a');
      link.href = objectUrl;
      link.download =
        document.file_name || document.title || 'documento-liquidacion';
      window.document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(objectUrl);
    } catch (error) {
      console.error('Error downloading settlement document:', error);
      toast.error('No se pudo descargar el documento');
    } finally {
      setDownloadLoadingId(null);
    }
  };

  if (isLoading) {
    return (
      <LoadingState label='Cargando información…' />
    );
  }

  if (mode === 'history') {
    return (
      <div className='mx-auto flex h-full min-h-0 w-full max-w-4xl flex-col gap-4'>
        <div className='flex shrink-0 flex-col gap-3 rounded-medium border border-default-200 p-4'>
            <Textarea
              label='Nuevo comentario'
              placeholder='Agrega contexto o una observación sobre la liquidación…'
              value={newComment}
              onValueChange={setNewComment}
              minRows={2}
            />
            <div className='flex justify-end'>
              <Button
                size='sm'
                color='primary'
                onPress={handleAddComment}
                isDisabled={!newComment.trim()}
              >
                Guardar comentario
              </Button>
            </div>
        </div>

        <div className='min-h-0 flex-1 space-y-3 overflow-y-auto pr-1'>
          {comments.length === 0 && (
            <EmptyState title='La liquidación todavía no tiene actividad registrada.' />
          )}
          {comments.map((item) => {
            const isAction = item.type === 'action';
            const isOwner = item.created_by?.id === userProfile?.id;
            return (
              <div
                key={item.id}
                className='flex flex-col gap-2 rounded-medium border border-default-200 p-4'
              >
                  <div className='flex flex-wrap items-start justify-between gap-2'>
                    <div>
                      <div className='flex items-center gap-2'>
                        <Chip size='sm' variant='flat'>
                          {isAction ? 'Acción' : 'Comentario'}
                        </Chip>
                        <span className='text-sm font-medium'>
                          {item.created_by?.first_name}{' '}
                          {item.created_by?.last_name}
                        </span>
                      </div>
                      <p className='mt-1 text-xs text-default-400'>
                        {format(new Date(item.created_at), 'dd/MM/yyyy HH:mm')}
                        {item.is_edited ? ' · editado' : ''}
                      </p>
                    </div>
                    {!isAction && isOwner && editingCommentId !== item.id && (
                      <div className='flex gap-1'>
                        <Button
                          isIconOnly
                          size='sm'
                          variant='light'
                          aria-label='Editar comentario'
                          onPress={() => {
                            setEditingCommentId(item.id);
                            setEditingContent(item.content || '');
                          }}
                        >
                          <Icon icon='tabler:edit' />
                        </Button>
                        <Button
                          isIconOnly
                          size='sm'
                          color='danger'
                          variant='light'
                          aria-label='Eliminar comentario'
                          onPress={() => handleDelete(item.id)}
                        >
                          <Icon icon='tabler:trash' />
                        </Button>
                      </div>
                    )}
                  </div>

                  {editingCommentId === item.id ? (
                    <div className='space-y-2'>
                      <Textarea
                        value={editingContent}
                        onValueChange={setEditingContent}
                        minRows={2}
                      />
                      <div className='flex justify-end gap-2'>
                        <Button
                          size='sm'
                          variant='light'
                          onPress={() => setEditingCommentId(null)}
                        >
                          Cancelar
                        </Button>
                        <Button
                          size='sm'
                          color='primary'
                          onPress={() => handleSaveComment(item.id)}
                          isDisabled={!editingContent.trim()}
                        >
                          Guardar
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <p className='whitespace-pre-wrap text-sm text-default-600'>
                      {item.content}
                    </p>
                  )}
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  return (
    <div
      className={`grid h-full min-h-0 gap-4 ${
        documentPreview
          ? 'grid-rows-[minmax(0,1fr)_minmax(260px,45%)] lg:grid-cols-[minmax(0,1fr)_minmax(420px,42%)] lg:grid-rows-1'
          : 'grid-cols-1'
      }`}
    >
      <div className='min-h-0 space-y-4 overflow-y-auto pr-1'>
        <div className='flex flex-col rounded-medium border border-default-200 p-4'>
            {!selectedFile ? (
              <button
                type='button'
                className={`rounded-lg border-2 border-dashed p-8 text-center transition-colors ${
                  isDragging
                    ? 'border-primary bg-primary-50'
                    : 'border-divider hover:border-primary'
                }`}
                onDragOver={(event) => {
                  event.preventDefault();
                  setIsDragging(true);
                }}
                onDragLeave={() => setIsDragging(false)}
                onDrop={(event) => {
                  event.preventDefault();
                  setIsDragging(false);
                  selectDocumentFile(event.dataTransfer.files?.[0] || null);
                }}
                onClick={() => fileInputRef.current?.click()}
              >
                <input
                  ref={fileInputRef}
                  type='file'
                  className='hidden'
                  onChange={(event) =>
                    selectDocumentFile(event.target.files?.[0] || null)
                  }
                />
                <Icon icon='tabler:upload' width={30} className='mx-auto mb-3 text-default-400' />
                <p className='text-sm font-medium'>Agregar documento</p>
                <p className='mt-1 text-xs text-default-400'>
                  Arrastra un archivo o selecciónalo desde tu equipo
                </p>
              </button>
            ) : (
              <div className='flex flex-wrap items-end gap-3'>
                <Input
                  label='Título del documento'
                  value={documentTitle}
                  onValueChange={setDocumentTitle}
                  className='min-w-64 flex-1'
                />
                <Button
                  color='primary'
                  onPress={handleUploadDocument}
                  isLoading={isUploading}
                  isDisabled={!documentTitle.trim()}
                >
                  Adjuntar
                </Button>
                <Button
                  variant='light'
                  onPress={() => selectDocumentFile(null)}
                >
                  Cancelar
                </Button>
              </div>
            )}
        </div>

        {documents.length === 0 && (
          <EmptyState title='No hay documentos asociados a esta liquidación.' />
        )}

        {documents.map((document) => {
          const isSourceDocument = Boolean(document.source_document_id);
          const isOwner = document.created_by?.id === userProfile?.id;
          return (
            <div
              key={document.id}
              className='flex flex-wrap items-center justify-between gap-3 rounded-medium border border-default-200 p-4'
            >
                <div className='flex min-w-0 items-center gap-3'>
                  <Icon icon='tabler:file' className='shrink-0 text-default-400' />
                  <div className='min-w-0'>
                    <div className='flex flex-wrap items-center gap-2'>
                      <p className='break-words text-sm font-medium'>
                        {document.title || document.file_name || 'Documento'}
                      </p>
                      {isSourceDocument && (
                        <Chip size='sm' variant='flat'>
                          Original
                        </Chip>
                      )}
                    </div>
                    <p className='mt-1 break-all text-xs text-default-400'>
                      {document.file_name} ·{' '}
                      {format(
                        new Date(document.created_at),
                        'dd/MM/yyyy HH:mm'
                      )}
                    </p>
                  </div>
                </div>
                <div className='flex items-center gap-1'>
                  <Button
                    isIconOnly
                    size='sm'
                    variant='flat'
                    aria-label='Previsualizar documento'
                    isLoading={previewLoadingId === document.id}
                    onPress={() => getPreview(document)}
                  >
                    <Icon icon='tabler:eye' />
                  </Button>
                  <Button
                    isIconOnly
                    size='sm'
                    variant='flat'
                    aria-label='Descargar documento'
                    isLoading={downloadLoadingId === document.id}
                    onPress={() => downloadDocument(document)}
                  >
                    <Icon icon='tabler:download' />
                  </Button>
                  {!isSourceDocument && isOwner && (
                    <Button
                      isIconOnly
                      size='sm'
                      color='danger'
                      variant='light'
                      aria-label='Eliminar documento'
                      onPress={() => handleDelete(document.id)}
                    >
                      <Icon icon='tabler:trash' />
                    </Button>
                  )}
                </div>
            </div>
          );
        })}
      </div>

      {documentPreview && (
        <SettlementSourcePreview
          remoteDocument={documentPreview}
          onClose={() => setDocumentPreview(null)}
        />
      )}
    </div>
  );
};

export default SettlementEditSupportPanel;
